/* ==========================================================================
   Translating what people wrote

   The interface language comes from a table we ship. This handles the other
   half: a message, a review, a note about the oven. One control translates a
   whole block at once, and tapping again puts the original back — people want
   to check what was actually said.
   ========================================================================== */

import { useEffect, useState } from 'react';
import { api } from '../api';
import { getLang, t } from '../lib/i18n';
import { state } from '../store';

/** Whether the server has a translation provider configured at all. */
let configured: boolean | null = null;
let languages: string[] = [];

export async function loadTranslationStatus(): Promise<void> {
  try {
    const s = await api.translateStatus();
    configured = s.configured;
    languages = s.languages;
  } catch {
    configured = false;
  }
}

/** Whether the control should be offered at all: a provider is set up and we
 *  can reach the language this person reads in.
 *
 *  This used to require the reader NOT to be in English, which quietly made
 *  the feature useless in the direction that matters most here. Oster reads
 *  English; the cleaners he is hiring often write Spanish. Under the old test
 *  he was the one person who could never translate anything. */
export const canTranslate = (): boolean =>
  configured === true && languages.includes(getLang());

/** Whether to translate without being asked.
 *
 *  Only for somebody not reading English. A cleaner who reads Spanish should
 *  find a home's note already in Spanish rather than having to discover a
 *  button. An English reader is shown the original, because most of what
 *  reaches them is already English and translating it on sight would spend a
 *  call and a moment's delay to change nothing. */
export const autoTranslate = (): boolean => canTranslate() && getLang() !== 'en';

/** Translate a fixed list of strings, in order.
 *
 *  Translated by default for anyone whose interface is not in English --
 *  a cleaner who reads Spanish should see a home's note in Spanish without
 *  finding a button first. "Show original" is still there, because people
 *  want to check what was actually said. */
export function useTranslated(texts: string[]) {
  const [showing, setShowing] = useState(false);
  const [out, setOut] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  /* Whether translating actually changed anything. English into English is a
     valid, successful, 200-OK translation that returns the sentence it was
     given -- so the button worked perfectly and appeared to do nothing, which
     is indistinguishable from broken. */
  const [changed, setChanged] = useState(true);
  const lang = getLang();
  const key = texts.join('\u0000');

  const fetchIt = async (): Promise<boolean> => {
    setBusy(true);
    setFailed(false);
    try {
      const res = await api.translate(texts, lang);
      if (res.ok) {
        setOut(res.translations);
        setChanged(res.translations.some((line, i) => line.trim() !== (texts[i] ?? '').trim()));
        return true;
      }
      setFailed(true);
      return false;
    } catch {
      setFailed(true);
      return false;
    } finally {
      setBusy(false);
    }
  };

  /* A new language, or different text, invalidates what we fetched -- and,
     when the reader is not in English and there is something to translate,
     fetches straight away. Empty text is skipped: nothing to translate. */
  useEffect(() => {
    setOut(null); setShowing(false); setFailed(false); setChanged(true);
    if (!autoTranslate() || !texts.some((t) => t.trim())) return;
    let alive = true;
    void fetchIt().then((ok) => { if (alive && ok) setShowing(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, key]);

  const toggle = async () => {
    if (showing) { setShowing(false); return; }
    if (out) { setShowing(true); return; }
    if (await fetchIt()) setShowing(true);
  };

  const view = showing && out ? out : texts;
  return { view, showing, busy, failed, changed, toggle, available: canTranslate() };
}

/** The control itself. Renders nothing when translation is unavailable. */
export function TranslateToggle({
  showing, busy, failed, changed = true, onToggle, compact,
}: {
  showing: boolean;
  busy: boolean;
  failed?: boolean;
  /** False once we have asked and the answer came back the same. */
  changed?: boolean;
  onToggle: () => void;
  compact?: boolean;
}) {
  if (!canTranslate()) return null;

  /* Nothing to do, and saying so is the whole point: a button that runs,
     succeeds and leaves the screen exactly as it was reads as broken. */
  if (!changed && !busy) {
    return <span className="hint">{t('tr_already')}</span>;
  }

  return (
    <div className="row" style={{ gap: 8, alignItems: 'center' }}>
      <button
        type="button"
        className={'btn quiet ' + (compact ? 'sm' : 'sm')}
        onClick={onToggle}
        disabled={busy}
      >
        {busy ? t('tr_busy') : showing ? t('tr_original') : t('tr_translate')}
      </button>
      {failed && <span className="hint">{t('tr_failed')}</span>}
    </div>
  );
}

/** A single block of text with its own control. */
export function TranslatableText({
  text, className, style,
}: { text: string; className?: string; style?: React.CSSProperties }) {
  const { view, showing, busy, failed, changed, toggle, available } = useTranslated([text]);
  return (
    <>
      <span className={className} style={style}>{view[0]}</span>
      {available && (
        <TranslateToggle
          showing={showing}
          busy={busy}
          failed={failed}
          changed={changed}
          onToggle={toggle}
          compact
        />
      )}
    </>
  );
}

/* Keep the status fresh when the interface language changes. */
export function translationLanguage(): string {
  return state.lang;
}
